import {
  COST_TYPES,
  type CostType,
  defaultPurpose,
  EXPENSE_PURPOSES,
  type ExpenseInput,
  type ExpensePurpose,
  expenseInput,
  FUEL_UNIT_DECIMALS,
  type FuelUnit,
  fillUpOptions,
  fromQty,
  isFillUpType,
  parseDecimal,
  parseTaxRate,
} from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDownIcon, PaperclipIcon, Trash2Icon } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DocumentList } from "@/components/app/attachments";
import { CreatableSelect } from "@/components/app/creatable-select";
import { Field } from "@/components/app/field";
import { MoneyInput } from "@/components/app/qty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/misc";
import { NONE, Select } from "@/components/ui/select";
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader } from "@/components/ui/sheet";
import { localToday, useFormat } from "@/lib/format";
import { useSystemInfo } from "@/lib/system";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { COST_TYPE_ICONS, PURPOSE_ICONS } from "./shared";

/** What the sheet edits: the expense's input, with the cost type still to pick on a new one. */
export type ExpenseDraft = {
  [K in Exclude<keyof ExpenseInput, "costType" | "amountMinor">]-?: Exclude<
    ExpenseInput[K],
    undefined
  >;
} & {
  costType: CostType | null;
  amountMinor: number | null;
};

type Expense = RouterOutputs["vehicleManagement"]["expenses"]["get"];

/** Where the sheet was opened from: a new expense starting with what that place knows. */
export type ExpenseSheetTarget =
  { kind: "new"; preset?: Partial<ExpenseDraft> } | { kind: "edit"; id: string };

export function emptyDraft(preset: Partial<ExpenseDraft> = {}): ExpenseDraft {
  return {
    vehicleId: null,
    spentOn: localToday(),
    costType: null,
    purpose: null,
    amountMinor: null,
    taxRateBp: null,
    supplierId: null,
    payeeName: null,
    workOrderId: null,
    mileage: null,
    fuelQty: null,
    fuelUnit: null,
    invoiceNumber: null,
    comment: null,
    ...preset,
  };
}

/** An expense as a draft, for editing it or for "Add again" (then dated today). */
export function draftOf(
  e:
    | Expense
    | RouterOutputs["vehicleManagement"]["expenses"]["list"]["months"][number]["rows"][number],
): ExpenseDraft {
  return {
    vehicleId: e.vehicle?.id ?? null,
    spentOn: e.spentOn,
    costType: e.costType,
    purpose: e.purpose,
    amountMinor: e.amountMinor,
    taxRateBp: e.taxRateBp,
    supplierId: e.supplier?.id ?? null,
    payeeName: e.payeeName,
    workOrderId: e.workOrder?.id ?? null,
    mileage: e.mileage,
    fuelQty: e.fuelQty,
    fuelUnit: e.fuelUnit,
    invoiceNumber: e.invoiceNumber,
    comment: e.comment,
  };
}

export function ExpenseSheet({
  target,
  onOpenChange,
  onDelete,
}: {
  target: ExpenseSheetTarget | null;
  onOpenChange: (open: boolean) => void;
  onDelete: (expense: Expense) => void;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  // A new expense saved to add a receipt carries on as that expense.
  const [savedId, setSavedId] = useState<string | null>(null);
  useEffect(() => {
    if (!target) setSavedId(null);
  }, [target]);
  const editId = target?.kind === "edit" ? target.id : savedId;
  const { data: existing } = useQuery({
    ...trpc.vehicleManagement.expenses.get.queryOptions({ id: editId ?? "" }),
    enabled: editId !== null,
  });
  const ready = target !== null && (editId === null || existing !== undefined);
  return (
    <Sheet open={target !== null} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader
          title={
            target?.kind === "edit"
              ? t("vehicleManagement.editExpense")
              : t("vehicleManagement.newExpense")
          }
        />
        {ready ? (
          <ExpenseForm
            key={`${target?.kind === "edit" ? target.id : "new"}`}
            initial={
              existing && target?.kind === "edit"
                ? draftOf(existing)
                : emptyDraft(target?.kind === "new" ? target.preset : {})
            }
            existing={existing ?? null}
            onClose={() => onOpenChange(false)}
            onSavedForReceipt={(id) => setSavedId(id)}
            onCancel={() => onOpenChange(false)}
            onDelete={(expense) => {
              onOpenChange(false);
              onDelete(expense);
            }}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function ExpenseForm({
  initial,
  existing,
  onClose,
  onSavedForReceipt,
  onCancel,
  onDelete,
}: {
  initial: ExpenseDraft;
  existing: Expense | null;
  onClose: () => void;
  onSavedForReceipt: (id: string) => void;
  onCancel: () => void;
  onDelete: (expense: Expense) => void;
}) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const uploads = useSystemInfo()?.capabilities.uploads ?? false;
  const ids = useId();
  const [draft, setDraft] = useState<ExpenseDraft>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof ExpenseDraft, string>>>({});
  const [allTypes, setAllTypes] = useState(false);
  const [details, setDetails] = useState(
    Boolean(
      initial.supplierId ||
      initial.payeeName ||
      initial.invoiceNumber ||
      initial.workOrderId ||
      (initial.mileage !== null && !(initial.costType && isFillUpType(initial.costType))),
    ),
  );
  const [purposeTouched, setPurposeTouched] = useState(initial.purpose !== null);
  // "Other day" stays chosen while its date is today's or yesterday's.
  const [otherDay, setOtherDay] = useState(false);
  const dateRef = useRef<HTMLInputElement>(null);
  const set = (patch: Partial<ExpenseDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      for (const key of Object.keys(patch)) delete next[key as keyof ExpenseDraft];
      return next;
    });
  };

  const { data: settings } = useQuery(trpc.settings.get.queryOptions());
  const { data: hints } = useQuery(trpc.vehicleManagement.expenses.hints.queryOptions());
  const { data: vehicles = [] } = useQuery(trpc.vehicles.list.queryOptions());
  const { data: suppliers = [] } = useQuery(trpc.suppliers.list.queryOptions());
  const { data: facts } = useQuery({
    ...trpc.vehicleManagement.vehicle.queryOptions({ vehicleId: draft.vehicleId ?? "" }),
    enabled: draft.vehicleId !== null,
  });
  const { data: workOrders = [] } = useQuery({
    ...trpc.workOrders.list.queryOptions({ vehicleId: draft.vehicleId, scope: "all" }),
    enabled: details && draft.vehicleId !== null,
  });
  const create = useMutation(trpc.vehicleManagement.expenses.create.mutationOptions());
  const update = useMutation(trpc.vehicleManagement.expenses.update.mutationOptions());
  const busy = create.isPending || update.isPending;

  const currency = settings?.currency ?? "EUR";
  const options = draft.vehicleId ? facts?.fillUpOptions : fillUpOptions(null);
  const offered = (type: CostType) =>
    type === draft.costType ||
    (type === "fuel"
      ? (options?.fuel.length ?? 1) > 0
      : type === "charging"
        ? (options?.charging ?? true)
        : true);
  const order = (hints?.costTypes ?? COST_TYPES).filter(offered);
  const shownTypes = allTypes ? order : order.slice(0, 6);
  if (draft.costType && !shownTypes.includes(draft.costType)) shownTypes.push(draft.costType);
  const fillUp = draft.costType !== null && isFillUpType(draft.costType);
  const units: readonly FuelUnit[] =
    draft.costType === "charging"
      ? ["kwh"]
      : draft.costType === "fuel"
        ? (options?.fuel ?? [])
        : [];
  const unitOf = (type: CostType | null): FuelUnit | null => {
    if (type === "charging") return "kwh";
    if (type !== "fuel") return null;
    const last = draft.vehicleId ? hints?.lastFuelUnit[draft.vehicleId] : undefined;
    const list = draft.vehicleId ? (facts?.fillUpOptions.fuel ?? []) : fillUpOptions(null).fuel;
    return last && list.includes(last) ? last : (list[0] ?? null);
  };
  const mileageUnit = facts?.mileageUnit ?? "km";

  const pickType = (type: CostType) => {
    const patch: Partial<ExpenseDraft> = { costType: type };
    if (!purposeTouched) patch.purpose = null;
    if (!isFillUpType(type)) {
      patch.fuelQty = null;
      patch.fuelUnit = null;
    } else if (
      !draft.fuelUnit ||
      !(type === "charging" ? draft.fuelUnit === "kwh" : draft.fuelUnit !== "kwh")
    ) {
      patch.fuelUnit = draft.fuelQty !== null ? unitOf(type) : null;
    }
    set(patch);
  };

  const purposeShown: ExpensePurpose =
    draft.purpose ?? (draft.costType ? defaultPurpose(draft.costType) : "running");

  async function save(mode: "close" | "again" | "receipt") {
    const parsed = expenseInput.safeParse({
      ...draft,
      fuelUnit: draft.fuelQty !== null ? (draft.fuelUnit ?? unitOf(draft.costType)) : null,
    });
    if (!parsed.success) {
      const next: Partial<Record<keyof ExpenseDraft, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof ExpenseDraft;
        if (!next[key])
          next[key] = issue.message.startsWith("validation.")
            ? issue.message
            : "validation.required";
      }
      setErrors(next);
      return;
    }
    let id: string;
    if (existing) {
      await update.mutateAsync({ id: existing.id, data: parsed.data });
      id = existing.id;
    } else {
      id = await create.mutateAsync(parsed.data);
    }
    toast.success(t("vehicleManagement.saved"));
    if (mode === "receipt") {
      onSavedForReceipt(id);
      return;
    }
    if (mode === "again") {
      setDraft(
        emptyDraft({
          vehicleId: draft.vehicleId,
          spentOn: draft.spentOn,
          supplierId: draft.supplierId,
          payeeName: draft.payeeName,
        }),
      );
      setPurposeTouched(false);
      return;
    }
    onClose();
  }

  const today = localToday();
  const yesterday = shiftDay(today, -1);
  const dateChoice =
    otherDay || (draft.spentOn !== today && draft.spentOn !== yesterday)
      ? "other"
      : draft.spentOn === today
        ? "today"
        : "yesterday";

  const payeeOptions = [
    ...suppliers.map((s) => ({ value: s.id, label: s.name })),
    ...[
      ...new Set([...(draft.payeeName ? [draft.payeeName] : []), ...(hints?.payeeNames ?? [])]),
    ].map((name) => ({
      value: `name:${name}`,
      label: name,
    })),
  ];

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        void save("close");
      }}
    >
      <SheetBody className="flex flex-col gap-5">
        <Field
          label={t("vehicleManagement.amount")}
          htmlFor={`${ids}-amount`}
          error={errors.amountMinor}
        >
          <MoneyInput
            id={`${ids}-amount`}
            currency={currency}
            showBasis={settings?.pricesIncludeTax === false}
            value={draft.amountMinor}
            onChange={(amountMinor) => set({ amountMinor })}
            invalid={Boolean(errors.amountMinor)}
            className="h-11 text-lg"
          />
        </Field>

        <Field label={t("vehicleManagement.costType")} error={errors.costType}>
          <div
            role="radiogroup"
            aria-label={t("vehicleManagement.costType")}
            className="grid grid-cols-3 gap-2 sm:grid-cols-4"
          >
            {shownTypes.map((type) => {
              const Icon = COST_TYPE_ICONS[type];
              const active = draft.costType === type;
              return (
                // biome-ignore lint/a11y/useSemanticElements: chips styled as buttons, announced as radios
                <button
                  key={type}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => pickType(type)}
                  className={cn(
                    "flex min-h-14 flex-col items-center justify-center gap-1 rounded-lg border px-1.5 py-2 text-center text-xs font-medium transition outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                    active
                      ? "border-brand bg-brand/10 text-foreground"
                      : "border-border bg-surface text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                  )}
                >
                  <Icon className={cn("size-4", active && "text-brand")} />
                  <span className="leading-tight">{t(`vehicleManagement.costTypes.${type}`)}</span>
                </button>
              );
            })}
            {order.length > 6 && !allTypes ? (
              <button
                type="button"
                onClick={() => setAllTypes(true)}
                className="flex min-h-14 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border px-1.5 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              >
                <ChevronDownIcon className="size-4" />
                {t("vehicleManagement.moreTypes")}
              </button>
            ) : null}
          </div>
        </Field>

        {fillUp ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("vehicleManagement.quantity")}
              htmlFor={`${ids}-qty`}
              error={errors.fuelQty ?? errors.fuelUnit}
              optional
            >
              <div className="flex gap-2">
                <QuantityField
                  id={`${ids}-qty`}
                  value={draft.fuelQty}
                  unit={draft.fuelUnit ?? unitOf(draft.costType) ?? "l"}
                  onChange={(fuelQty) =>
                    set({
                      fuelQty,
                      fuelUnit:
                        fuelQty === null ? null : (draft.fuelUnit ?? unitOf(draft.costType)),
                    })
                  }
                />
                {units.length > 1 ? (
                  <Select
                    aria-label={t("vehicleManagement.unit")}
                    className="w-28 shrink-0"
                    value={draft.fuelUnit ?? unitOf(draft.costType) ?? units[0] ?? "l"}
                    onValueChange={(fuelUnit) => set({ fuelUnit })}
                    options={units.map((u) => ({
                      value: u,
                      label: t(`vehicleManagement.fuelUnits.${u}`),
                    }))}
                  />
                ) : (
                  <span className="flex shrink-0 items-center text-sm text-muted-foreground">
                    {t(`vehicleManagement.fuelUnits.${units[0] ?? "kwh"}`)}
                  </span>
                )}
              </div>
            </Field>
            {draft.vehicleId ? (
              <MileageField
                id={`${ids}-mileage`}
                value={draft.mileage}
                unit={mileageUnit}
                error={errors.mileage}
                onChange={(mileage) => set({ mileage })}
              />
            ) : null}
          </div>
        ) : null}

        <Field label={t("vehicleManagement.date")} htmlFor={`${ids}-date`} error={errors.spentOn}>
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              aria-label={t("vehicleManagement.date")}
              value={dateChoice}
              onChange={(choice) => {
                setOtherDay(choice === "other");
                if (choice === "today") set({ spentOn: today });
                else if (choice === "yesterday") set({ spentOn: yesterday });
                else {
                  // The field appears with the date picked so far; the browser's picker opens on it.
                  requestAnimationFrame(() => {
                    const input = dateRef.current;
                    input?.focus();
                    try {
                      input?.showPicker();
                    } catch {
                      // Not every browser opens it from a script; the focused field is enough.
                    }
                  });
                }
              }}
              options={[
                { value: "today", label: t("vehicleManagement.today") },
                { value: "yesterday", label: t("vehicleManagement.yesterday") },
                { value: "other", label: t("vehicleManagement.otherDay") },
              ]}
            />
            {dateChoice === "other" ? (
              <Input
                ref={dateRef}
                id={`${ids}-date`}
                type="date"
                aria-label={t("vehicleManagement.date")}
                className="w-auto"
                value={draft.spentOn}
                onChange={(event) => event.target.value && set({ spentOn: event.target.value })}
              />
            ) : null}
          </div>
        </Field>

        <Field
          label={t("vehicleManagement.comment")}
          htmlFor={`${ids}-comment`}
          error={errors.comment}
          optional
        >
          <Input
            id={`${ids}-comment`}
            placeholder={t("vehicleManagement.commentPlaceholder")}
            value={draft.comment ?? ""}
            onChange={(event) => set({ comment: event.target.value || null })}
          />
        </Field>

        <Field label={t("vehicleManagement.purpose")}>
          <Segmented
            aria-label={t("vehicleManagement.purpose")}
            className="flex-wrap"
            value={purposeShown}
            onChange={(purpose) => {
              setPurposeTouched(true);
              set({ purpose });
            }}
            options={EXPENSE_PURPOSES.map((purpose) => {
              const Icon = PURPOSE_ICONS[purpose];
              return {
                value: purpose,
                label: (
                  <>
                    <Icon className="size-3.5" />
                    {t(`vehicleManagement.purposes.${purpose}`)}
                  </>
                ),
              };
            })}
          />
        </Field>

        <section className="flex flex-col gap-4">
          <button
            type="button"
            aria-expanded={details}
            onClick={() => setDetails((d) => !d)}
            className="flex items-center gap-1.5 self-start text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <ChevronDownIcon
              className={cn("size-4 transition-transform", !details && "-rotate-90")}
            />
            {t("vehicleManagement.moreDetails")}
          </button>
          {details ? (
            <div className="flex flex-col gap-4">
              <Field label={t("vehicleManagement.payee")} htmlFor={`${ids}-payee`} optional>
                <CreatableSelect
                  id={`${ids}-payee`}
                  value={draft.supplierId ?? (draft.payeeName ? `name:${draft.payeeName}` : null)}
                  onChange={(value) =>
                    value?.startsWith("name:")
                      ? set({ supplierId: null, payeeName: value.slice(5) })
                      : set({ supplierId: value, payeeName: null })
                  }
                  placeholder={t("vehicleManagement.payeePlaceholder")}
                  searchPlaceholder={t("common.search")}
                  options={payeeOptions}
                  createLabel={(text) => t("vehicleManagement.payeeUse", { name: text })}
                  onCreate={(text) => Promise.resolve(`name:${text}`)}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("vehicleManagement.vehicle")} htmlFor={`${ids}-vehicle`}>
                  <Select
                    id={`${ids}-vehicle`}
                    value={draft.vehicleId ?? NONE}
                    onValueChange={(v) =>
                      set({
                        vehicleId: v === NONE ? null : v,
                        workOrderId: null,
                        ...(v === NONE ? { mileage: null } : {}),
                      })
                    }
                    options={[
                      { value: NONE, label: t("vehicleManagement.wholeGarage") },
                      ...vehicles.map((v) => ({ value: v.id, label: v.name })),
                    ]}
                  />
                </Field>
                {draft.vehicleId ? (
                  <Field label={t("vehicleManagement.workOrder")} htmlFor={`${ids}-wo`} optional>
                    <Select
                      id={`${ids}-wo`}
                      value={draft.workOrderId ?? NONE}
                      onValueChange={(v) => set({ workOrderId: v === NONE ? null : v })}
                      options={[
                        { value: NONE, label: t("common.none") },
                        ...workOrders.map((w) => ({ value: w.id, label: w.title })),
                      ]}
                    />
                  </Field>
                ) : null}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {draft.vehicleId && !fillUp ? (
                  <MileageField
                    id={`${ids}-mileage`}
                    value={draft.mileage}
                    unit={mileageUnit}
                    error={errors.mileage}
                    onChange={(mileage) => set({ mileage })}
                  />
                ) : null}
                <Field
                  label={t("vehicleManagement.invoiceNumber")}
                  htmlFor={`${ids}-invoice`}
                  optional
                >
                  <Input
                    id={`${ids}-invoice`}
                    value={draft.invoiceNumber ?? ""}
                    onChange={(event) => set({ invoiceNumber: event.target.value || null })}
                  />
                </Field>
                {settings?.pricesIncludeTax === false ? (
                  <Field
                    label={t("vehicleManagement.taxRate")}
                    htmlFor={`${ids}-rate`}
                    error={errors.taxRateBp}
                    optional
                  >
                    <Input
                      id={`${ids}-rate`}
                      inputMode="decimal"
                      placeholder={settings ? format.taxRate(settings.taxRateToday) : undefined}
                      defaultValue={
                        draft.taxRateBp === null ? "" : format.inputNumber(draft.taxRateBp / 100, 2)
                      }
                      onChange={(event) => {
                        const text = event.target.value.trim();
                        const rate = text ? parseTaxRate(text) : null;
                        set({ taxRateBp: rate });
                        if (text && rate === null)
                          setErrors((e) => ({ ...e, taxRateBp: "validation.taxRate" }));
                      }}
                    />
                  </Field>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>

        {existing ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">{t("vehicleManagement.receipts")}</h3>
            <DocumentList
              ownerType="expense"
              ownerId={existing.id}
              attachments={existing.attachments}
            />
          </section>
        ) : uploads ? (
          <Button
            type="button"
            variant="outline"
            className="self-start"
            disabled={busy}
            onClick={() => void save("receipt")}
          >
            <PaperclipIcon />
            {t("vehicleManagement.addReceipt")}
          </Button>
        ) : null}
      </SheetBody>
      <SheetFooter className="flex-wrap">
        {existing ? (
          <Button
            type="button"
            variant="ghost"
            className="mr-auto text-destructive"
            onClick={() => onDelete(existing)}
          >
            <Trash2Icon />
            {t("common.delete")}
          </Button>
        ) : (
          <Button type="button" variant="ghost" className="mr-auto" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        )}
        {existing ? null : (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => void save("again")}
          >
            {t("vehicleManagement.saveAndAnother")}
          </Button>
        )}
        <Button type="submit" disabled={busy}>
          {t("common.save")}
        </Button>
      </SheetFooter>
    </form>
  );
}

function shiftDay(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** A fill-up's quantity: "42,51" in milli-units, with the unit's decimals. */
function QuantityField({
  id,
  value,
  unit,
  onChange,
}: {
  id: string;
  value: number | null;
  unit: FuelUnit;
  onChange: (value: number | null) => void;
}) {
  const format = useFormat();
  const toText = (v: number | null) =>
    v === null ? "" : format.inputNumber(fromQty(v), FUEL_UNIT_DECIMALS[unit]);
  const [text, setText] = useState(() => toText(value));
  return (
    <Input
      id={id}
      inputMode="decimal"
      autoComplete="off"
      className="tabular"
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        if (!event.target.value.trim()) return onChange(null);
        const amount = parseDecimal(event.target.value);
        if (amount !== null && amount > 0) onChange(Math.round(amount * 1000));
      }}
      onBlur={() => setText(toText(value))}
    />
  );
}

function MileageField({
  id,
  value,
  unit,
  error,
  onChange,
}: {
  id: string;
  value: number | null;
  unit: string;
  error?: string;
  onChange: (value: number | null) => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <Field label={t("vehicleManagement.mileage")} htmlFor={id} error={error} optional>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          inputMode="numeric"
          className="tabular"
          value={value ?? ""}
          onChange={(event) => {
            const digits = event.target.value.replace(/\D/g, "");
            onChange(digits ? Number(digits) : null);
          }}
        />
        <span className="shrink-0 text-sm text-muted-foreground">{unit}</span>
      </div>
    </Field>
  );
}
